import { useEffect, lazy, Suspense } from 'react';
import { Toaster } from "@/components/ui/sonner";
import { PERMISSIONS } from '@/constants/permissions';
import { env } from '@/lib/env';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import { LoginPage } from './pages/login';
import { ForgotPasswordPage } from './pages/forgot-password';
import { SignUpPage } from './pages/signup';
import ResetPasswordPage from './pages/reset-password';
import { ProtectedRoute } from './components/auth/protected-route';
import { PermissionRoute } from './components/auth/permission-route';
import { useAuthStore } from './stores/auth-store';
import { isPublicPath } from './lib/public-routes';
import { LoadingFallback } from './components/common/loading-fallback';

// Lazy Imports
const Dashboard = lazy(() => import('./pages/dashboard/dashboard').then(m => ({ default: m.Dashboard })));
const CalendarPage = lazy(() => import('./pages/calendar').then(m => ({ default: m.CalendarPage })));
const AccountsPage = lazy(() => import('./pages/financial-accounts').then(m => ({ default: m.AccountsPage })));
const OfficePage = lazy(() => import('./pages/office').then(m => ({ default: m.OfficePage })));
const ContactsPage = lazy(() => import('./pages/contacts').then(m => ({ default: m.ContactsPage })));
const LawsuitsPage = lazy(() => import('./pages/lawsuits').then(m => ({ default: m.LawsuitsPage })));
const LawsuitDetailsPage = lazy(() => import('./pages/lawsuits/lawsuit-details-page').then(m => ({ default: m.LawsuitDetailsPage })));
const ProfilePage = lazy(() => import('./pages/profile').then(m => ({ default: m.ProfilePage })));
const DashboardEditorPage = lazy(() => import('./pages/config/dashboard-editor-page').then(m => ({ default: m.DashboardEditorPage })));
const SupportPage = lazy(() => import('./pages/support').then(m => ({ default: m.SupportPage })));
const AuditLogsPage = lazy(() => import('./pages/audit').then(m => ({ default: m.AuditLogsPage })));
const TimesheetPage = lazy(() => import('./pages/timesheet').then(m => ({ default: m.TimesheetPage })));
const BillingPage = lazy(() => import('./pages/billing').then(m => ({ default: m.BillingPage })));
const AuthCallbackPage = lazy(() =>
  import('./pages/auth-callback').then((m) => ({ default: m.AuthCallbackPage })),
);
const ActivationPage = lazy(() =>
  import('./pages/activation').then((m) => ({ default: m.ActivationPage })),
);
const NotificationsPage = lazy(() => import('./pages/notifications'));

function App() {
  const { me, token } = useAuthStore();

  useEffect(() => {
    // Em rota pública a sessão salva pode estar expirada; validá-la aqui
    // disparava refresh → redirect para /login e derrubava o callback do SSO.
    // O /auth/callback chama me() por conta própria depois de trocar o token.
    if (token && !isPublicPath(window.location.pathname)) {
      me();
    }
  }, [me, token]);

  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignUpPage />} />
            {/* A landing page aponta "Criar conta" para /acesso/nova-conta,
                rota que nunca existiu aqui — caía no catch-all e jogava quem
                queria se cadastrar na tela de login. O link já circulou por
                WhatsApp, então o alias fica mesmo depois de corrigir a LP. */}
            <Route path="/acesso/nova-conta" element={<Navigate to="/signup" replace />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/acesso/nova-senha/:uid/:token" element={<ResetPasswordPage />} />
            <Route path="/acesso/convidado/:uid/:token" element={<ResetPasswordPage mode="invite" />} />
            <Route path="/acesso/entrar/:uid/:token" element={<ActivationPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />

            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            
            <Route path="/calendar" element={
              <PermissionRoute permission={PERMISSIONS.CORE.VIEW_EVENT}>
                <CalendarPage />
              </PermissionRoute>
            } />
            
            <Route path="/financial-accounts" element={
              <PermissionRoute permission={PERMISSIONS.CORE.VIEW_PAYMENT}>
                <AccountsPage />
              </PermissionRoute>
            } />
            
            <Route path="/offices" element={
              <PermissionRoute permission={PERMISSIONS.CORE.VIEW_OFFICE}>
                <OfficePage />
              </PermissionRoute>
            } />
            
            <Route path="/contacts" element={
              <PermissionRoute permission={PERMISSIONS.CORE.VIEW_CONTACT}>
                <ContactsPage />
              </PermissionRoute>
            } />
            
            <Route path="/lawsuits" element={
              <PermissionRoute permission={PERMISSIONS.LAWSUIT.VIEW_LAWSUIT}>
                <LawsuitsPage />
              </PermissionRoute>
            } />
            
            <Route path="/lawsuits/:id" element={
              <PermissionRoute permission={PERMISSIONS.LAWSUIT.VIEW_LAWSUIT}>
                <LawsuitDetailsPage />
              </PermissionRoute>
            } />

            <Route path="/timesheet" element={
              <PermissionRoute permission={PERMISSIONS.CORE.VIEW_TIMESHEET}>
                <TimesheetPage />
              </PermissionRoute>
            } />

            {env.VITE_ENABLE_BILLING && (
              <Route path="/billing" element={
                <PermissionRoute permission={PERMISSIONS.CORE.VIEW_BILLABLEITEM}>
                  <BillingPage />
                </PermissionRoute>
              } />
            )}

            <Route path="/config/dashboard" element={
                <ProtectedRoute>
                    <DashboardEditorPage />
                </ProtectedRoute>
            } />
            
            <Route path="/audit-logs" element={
              <PermissionRoute permission={PERMISSIONS.CORE.VIEW_AUDITLOG}>
                <AuditLogsPage />
              </PermissionRoute>
            } />
            
            <Route path="/notificacoes" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
            
            <Route path="*" element={<Navigate to="/login" />} />
          </Routes>
      </Suspense>
      <Toaster position="top-right" />
    </BrowserRouter>
  );
}

export default App;
